// Shared dropdown primitives extracted from dashboard.jsx. Used across many
// screens (audiences, creatives, events, admin, create wizard). Visual is
// .dd / .dd-trigger / .dd-pop / .dd-item from styles.css.

import { Fragment, useEffect, useId, useRef, useState } from 'react';
import type { KeyboardEvent, RefObject } from 'react';
import { ResponsivePicker } from './responsive-picker';
import { Icons } from './primitives';
import { fmtDate as fmtDateNy, fmtDateInput, nyMidnightMs, nyParts, nyDateInputToMs } from '../lib/datetime';
import { usePhone } from '../lib/responsive';
import { DROPDOWN_MIN_WIDTH_PX, menuPosition, menuVertical } from './dropdown-geometry';
import type { MenuPosition, MenuVertical } from './dropdown-geometry';

/** `.dd-pop`'s own cap; a menu may be shorter when its trigger sits near a screen edge. */
const MENU_MAX_HEIGHT_PX = 360;

/**
 * Where a menu sits vertically: downward under its trigger, or upward when the
 * trigger is near the bottom of the screen (the C1 MultiSelect rule). On a phone
 * the picker is a sheet the stylesheet sizes, so it keeps its plain `top`.
 */
function verticalStyle(phone: boolean, pos: MenuPosition, vertical: MenuVertical | null) {
  if (phone || !vertical) return { top: pos.top };
  return { top: vertical.top ?? 'auto', bottom: vertical.bottom ?? 'auto', maxHeight: vertical.maxHeight };
}

export function useOutsideClick(ref: RefObject<HTMLElement>, onClose: () => void) {
  useEffect(() => {
    // A target that is not a Node (none in practice) is outside by definition,
    // as `contains(null)` answered before this was typed.
    const handler = (e: MouseEvent) => {
      if (ref.current && !(e.target instanceof Node && ref.current.contains(e.target))) onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [ref, onClose]);
}

export interface DropdownOption<T extends string = string> {
  id: T;
  label: string;
  dot?: string;
  swatch?: string;
  /// Options sharing a `section` render under one heading; a heading is drawn
  /// wherever the section changes from the option before.
  section?: string;
}

export interface DropdownProps<T extends string = string> {
  label?: string;
  value: T;
  options: readonly DropdownOption<T>[];
  onChange: (value: T) => void;
  rightAlign?: boolean;
  width?: number;
  ariaLabel?: string;
  disabled?: boolean;
}

/// `width` was previously accepted by four call sites and destructured by none,
/// so `width={210}` on the sort and range pickers has silently done nothing.
/// It is honoured now, which also makes `rightAlign` correct: the right edge can
/// only be aligned against a width this component actually knows.
export function Dropdown<T extends string = string>({
  label = undefined,
  value,
  options,
  onChange,
  rightAlign = false,
  width = undefined,
  ariaLabel = undefined,
  disabled = false,
}: DropdownProps<T>) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [pos, setPos] = useState<MenuPosition>({ top: 0, left: 0, width: 0 });
  const [vertical, setVertical] = useState<MenuVertical | null>(null);
  const phone = usePhone();
  const ref = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const optionRefs = useRef<(HTMLDivElement | null)[]>([]);
  const listboxId = useId();
  useOutsideClick(ref, () => setOpen(false));
  // Array.isArray narrows to any[]; the annotation keeps the element type.
  const safeOptions: readonly DropdownOption<T>[] = Array.isArray(options) ? options : [];
  const isDisabled = disabled || safeOptions.length === 0;
  const selectedIndex = safeOptions.findIndex((option) => option.id === value);
  const cur = safeOptions[selectedIndex] || safeOptions[0];

  const closeMenu = (returnFocus = false) => {
    setOpen(false);
    if (returnFocus) triggerRef.current?.focus();
  };
  const openIt = (fallbackIndex = 0) => {
    if (isDisabled) return;
    if (open) {
      closeMenu();
    } else {
      if (triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect();
        setPos(menuPosition(
          rect,
          width,
          window.innerWidth,
          { minWidth: DROPDOWN_MIN_WIDTH_PX, align: rightAlign ? 'right' : 'left' },
        ));
        setVertical(menuVertical(rect, window.innerHeight, MENU_MAX_HEIGHT_PX));
      }
      setActiveIndex(selectedIndex >= 0 ? selectedIndex : fallbackIndex);
      setOpen(true);
    }
  };
  const moveActive = (nextIndex: number) => {
    if (safeOptions.length === 0) return;
    setActiveIndex((nextIndex + safeOptions.length) % safeOptions.length);
  };
  const selectOption = (index: number, returnFocus: boolean) => {
    const option = safeOptions[index];
    if (!option || isDisabled) return;
    onChange(option.id);
    closeMenu(returnFocus);
  };

  useEffect(() => {
    if (!open) return;
    if (safeOptions.length === 0) {
      setOpen(false);
      return;
    }
    const boundedIndex = activeIndex < 0
      ? 0
      : activeIndex >= safeOptions.length
        ? safeOptions.length - 1
        : activeIndex;
    if (boundedIndex !== activeIndex) {
      setActiveIndex(boundedIndex);
      return;
    }
    optionRefs.current[boundedIndex]?.focus();
  }, [activeIndex, open, safeOptions.length]);

  const onTriggerKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (isDisabled) return;
    if (event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (open) return;
      openIt(event.key === 'ArrowUp' ? safeOptions.length - 1 : 0);
    } else if (event.key === 'Escape' && open) {
      event.preventDefault();
      closeMenu(true);
    }
  };

  const onOptionKeyDown = (event: KeyboardEvent<HTMLDivElement>, index: number) => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        moveActive(index + 1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        moveActive(index - 1);
        break;
      case 'Home':
        event.preventDefault();
        moveActive(0);
        break;
      case 'End':
        event.preventDefault();
        moveActive(safeOptions.length - 1);
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        selectOption(index, true);
        break;
      case 'Escape':
        event.preventDefault();
        closeMenu(true);
        break;
      case 'Tab':
        setOpen(false);
        break;
      default:
        break;
    }
  };

  return (
    <div className="dd" ref={ref}>
      <button
        id={`${listboxId}-trigger`}
        ref={triggerRef}
        className={`dd-trigger ${open ? 'open' : ''}`}
        onClick={() => openIt()}
        type="button"
        aria-label={ariaLabel}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-controls={open ? listboxId : undefined}
        aria-disabled={isDisabled || undefined}
        disabled={isDisabled}
        onKeyDown={onTriggerKeyDown}
      >
        {label && <span className="lbl">{label}</span>}
        {cur?.dot && <span className="dot" style={{ width: 7, height: 7, borderRadius: '50%', background: cur.dot }}/>}
        {cur?.swatch && <span style={{ width: 10, height: 10, borderRadius: 2, background: cur.swatch, flexShrink: 0 }}/>}
        <span className="val">{cur?.label}</span>
        <span className="chev">{Icons.chev_d}</span>
      </button>
      {open && (
        <ResponsivePicker label={ariaLabel || label || "Choose an option"} onClose={() => setOpen(false)}><div
          id={listboxId}
          className="dd-pop"
          role="listbox"
          aria-labelledby={`${listboxId}-trigger`}
          style={{ ...verticalStyle(phone, pos, vertical), left: pos.left, minWidth: pos.width }}
        >
          {safeOptions.map((o, index) => (
            <Fragment key={o.id}>
              {o.section && o.section !== safeOptions[index - 1]?.section && (
                <div className="dd-sect" role="presentation">{o.section}</div>
              )}
              <div
                id={`${listboxId}-option-${index}`}
                ref={(node) => { optionRefs.current[index] = node; }}
                className={`dd-item ${o.id === value ? 'on' : ''}`}
                onClick={() => selectOption(index, true)}
                onFocus={() => setActiveIndex(index)}
                onKeyDown={(event) => onOptionKeyDown(event, index)}
                role="option"
                aria-selected={o.id === value}
                tabIndex={index === activeIndex ? 0 : -1}
              >
                {o.dot && <span className="dot" style={{ background: o.dot }}/>}
                {o.swatch && <span style={{ width: 10, height: 10, borderRadius: 2, background: o.swatch, flexShrink: 0 }}/>}
                <span>{o.label}</span>
                {o.id === value && <span className="check">{Icons.check}</span>}
              </div>
            </Fragment>
          ))}
        </div></ResponsivePicker>
      )}
    </div>
  );
}

export interface SearchableDropdownOption {
  id: string;
  label: string;
  sublabel?: string;
  meta?: string;
  dot?: string;
  swatch?: string;
}

export interface SearchableDropdownProps {
  value: string;
  options: SearchableDropdownOption[];
  onChange: (value: string) => void;
  onSearchChange?: (query: string) => void;
  placeholder?: string;
  emptyOptionLabel?: string | null;
  emptyOptionValue?: string;
  searchPlaceholder?: string;
  width?: string | number;
  popWidth?: number | null;
  size?: 'sm' | 'md';
  triggerClassName?: string;
  disabled?: boolean;
  ariaLabel?: string;
  /** A small caps label inside the trigger, as `Dropdown`'s (`PRODUCTION`). */
  label?: string;
}

export function SearchableDropdown({
  label = undefined,
  value,
  options,
  onChange,
  onSearchChange = undefined,
  placeholder = '— Select —',
  emptyOptionLabel = null,
  emptyOptionValue = '',
  searchPlaceholder = 'Search…',
  width = '100%',
  popWidth = null,
  size = 'md',
  triggerClassName = '',
  disabled = false,
  ariaLabel = undefined,
}: SearchableDropdownProps) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [pos, setPos] = useState<MenuPosition>({ top: 0, left: 0, width: 0 });
  const [vertical, setVertical] = useState<MenuVertical | null>(null);
  const phone = usePhone();
  const wrapRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  useOutsideClick(wrapRef, () => setOpen(false));

  const cur = options.find((o) => o.id === value);

  const close = () => {
    setOpen(false);
    window.setTimeout(() => triggerRef.current?.focus(), 0);
  };
  const selectOption = (nextValue: string) => {
    onChange(nextValue);
    close();
  };
  const openIt = () => {
    if (disabled) return;
    if (!open && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPos(menuPosition(
        rect,
        popWidth ?? undefined,
        window.innerWidth,
      ));
      setVertical(menuVertical(rect, window.innerHeight, MENU_MAX_HEIGHT_PX));
    }
    setOpen((o) => !o);
  };

  useEffect(() => {
    if (open) { setQ(''); setTimeout(() => searchRef.current?.focus(), 20); }
  }, [open]);

  useEffect(() => {
    if (!onSearchChange) return;
    const timer = window.setTimeout(
      () => onSearchChange(q.trim()),
      250,
    );
    return () => window.clearTimeout(timer);
  }, [onSearchChange, q]);

  const needle = q.trim().toLowerCase();
  const filtered = !needle
    ? options
    : options.filter((o) => {
        const hay = `${o.label || ''} ${o.sublabel || ''} ${o.meta || ''}`.toLowerCase();
        return hay.includes(needle);
      });

  const padY = size === 'sm' ? '4px 8px' : '6px 10px';
  const fontSize = size === 'sm' ? 11.5 : 12;

  return (
    <div className="dd" ref={wrapRef} style={{ display: 'block', width }}>
      <button
        ref={triggerRef}
        className={`dd-trigger ${open ? 'open' : ''} ${triggerClassName}`}
        onClick={openIt}
        style={{ width: '100%', padding: padY, fontSize }}
        type="button"
        disabled={disabled}
        aria-disabled={disabled || undefined}
        aria-label={ariaLabel}
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        {label && <span className="lbl">{label}</span>}
        {cur?.dot && <span className="dot" style={{ width: 7, height: 7, borderRadius: '50%', background: cur.dot }}/>}
        {cur?.swatch && <span style={{ width: 10, height: 10, borderRadius: 2, background: cur.swatch, flexShrink: 0 }}/>}
        <span className="val" style={{ color: cur ? 'var(--ink)' : 'var(--ink-3)', fontWeight: cur ? 600 : 500 }}>
          {cur ? cur.label : placeholder}
        </span>
        <span className="chev">{Icons.chev_d}</span>
      </button>
      {open && (
        <ResponsivePicker label={ariaLabel || label || "Choose options"} onClose={() => setOpen(false)}><div
          className="dd-pop"
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              event.preventDefault();
              close();
            }
          }}
          style={{ maxHeight: MENU_MAX_HEIGHT_PX, ...verticalStyle(phone, pos, vertical), left: pos.left, minWidth: pos.width, padding: 0, display: 'flex', flexDirection: 'column' }}
        >
          <div className="dd-search-row" style={{ padding: '8px 8px 6px', borderBottom: '1px solid var(--line)', display: 'flex', alignItems: 'center', gap: 6, background: 'var(--bg-elev)' }}>
            <span style={{ color: 'var(--ink-3)', display: 'inline-flex' }}>{Icons.search}</span>
            <input
              ref={searchRef}
              className="dd-search-input"
              placeholder={searchPlaceholder}
              aria-label={searchPlaceholder}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              style={{ flex: 1, background: 'transparent', border: 0, outline: 0, fontSize: 12, color: 'var(--ink)', padding: '2px 0' }}
            />
            {q && (
              <button
                className="btn btn-ghost btn-sm"
                aria-label="Clear search"
                style={{ padding: '2px 6px', fontSize: 10 }}
                onClick={() => { setQ(''); searchRef.current?.focus(); }}
                type="button"
              >{Icons.x}</button>
            )}
          </div>
          <div role="listbox" aria-label={ariaLabel || 'Options'} style={{ overflowY: 'auto', flex: 1, padding: 4 }}>
            {emptyOptionLabel !== null && !needle && (
              <div
                className={`dd-item ${value === emptyOptionValue ? 'on' : ''}`}
                onClick={() => selectOption(emptyOptionValue)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    selectOption(emptyOptionValue);
                  }
                }}
                role="option"
                aria-selected={value === emptyOptionValue}
                tabIndex={0}
                style={{ fontStyle: 'italic', color: 'var(--ink-2)' }}
              >
                <span>{emptyOptionLabel}</span>
                {value === emptyOptionValue && <span className="check">{Icons.check}</span>}
              </div>
            )}
            {filtered.length === 0 ? (
              <div className="muted" style={{ padding: '14px 12px', fontSize: 11.5, textAlign: 'center' }}>
                No matches for <b>"{q}"</b>.
              </div>
            ) : (
              filtered.map((o) => (
                <div
                  key={o.id}
                  className={`dd-item ${o.id === value ? 'on' : ''}`}
                  onClick={() => selectOption(o.id)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      selectOption(o.id);
                    }
                  }}
                  role="option"
                  aria-selected={o.id === value}
                  tabIndex={0}
                  style={{ alignItems: o.sublabel ? 'flex-start' : 'center', padding: '6px 10px' }}
                >
                  {o.dot && <span className="dot" style={{ background: o.dot, marginTop: o.sublabel ? 5 : 0 }}/>}
                  {o.swatch && <span style={{ width: 10, height: 10, borderRadius: 2, background: o.swatch, flexShrink: 0, marginTop: o.sublabel ? 4 : 0 }}/>}
                  <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{o.label}</span>
                    {o.sublabel && <span className="muted" style={{ fontSize: 10.5, fontFamily: 'var(--font-mono)', letterSpacing: '.04em' }}>{o.sublabel}</span>}
                  </div>
                  {o.meta && <span className="muted mono" style={{ fontSize: 10.5, marginLeft: 8, whiteSpace: 'nowrap' }}>{o.meta}</span>}
                  {o.id === value && <span className="check" style={{ marginLeft: 8 }}>{Icons.check}</span>}
                </div>
              ))
            )}
          </div>
        </div></ResponsivePicker>
      )}
    </div>
  );
}

// Date filter helpers — ALL boundaries are America/New_York (see lib/datetime).
// `start`/`end` are the NY-midnight instants of the boundary days; consumers
// serialize via nyStartOfDayIso/nyEndOfDayIso.

function fmtDate(d: Date) {
  return fmtDateNy(d); // NY
}
function fmtInput(d: Date) {
  return fmtDateInput(d); // NY YYYY-MM-DD for <input type="date">
}

// Calendar arithmetic in UTC-space keeps it tz-clean (no browser-local drift);
// the resulting y/m/d is then snapped to NY midnight.
function calDate(y: number, m: number, d: number) { return new Date(Date.UTC(y, m - 1, d)); }
function ymd(dt: Date) { return { y: dt.getUTCFullYear(), m: dt.getUTCMonth() + 1, d: dt.getUTCDate() }; }

export const DATE_PRESETS = [
  { id: 'today', label: 'Today' },
  { id: 'yesterday', label: 'Yesterday' },
  { id: 'last7', label: 'Last 7 days' },
  { id: 'last30', label: 'Last 30 days' },
  { id: 'mtd', label: 'Month to date' },
  { id: 'lastmonth', label: 'Last month' },
  { id: 'qtd', label: 'Quarter to date' },
  { id: 'lastq', label: 'Last quarter' },
];

export interface DateRangeBounds {
  start: Date;
  end: Date;
}

/// What `DateFilter` shows and emits. `preset` is a `DATE_PRESETS` id or
/// `'custom'`; an unknown id resolves like `last30` (`resolveRange`'s default).
export interface DateFilterValue extends DateRangeBounds {
  preset: string;
}

/// `disabled` is present only when `completedDaysOnly` was asked for: a
/// to-date preset on the first day of its period has no completed day yet.
export interface ResolvedRange extends DateRangeBounds {
  disabled?: boolean;
}

export function resolveRange(
  preset: string,
  now: Date = new Date(),
  { completedDaysOnly = false }: { completedDaysOnly?: boolean } = {},
): ResolvedRange {
  const p = nyParts(now); // NY "today"
  const today = calDate(p.y, p.m, p.d);
  const s = new Date(today), e = new Date(today);
  let disabled = false;
  switch (preset) {
    case 'today': break;
    case 'yesterday': s.setUTCDate(today.getUTCDate() - 1); e.setUTCDate(today.getUTCDate() - 1); break;
    case 'last7':
      if (completedDaysOnly) {
        e.setUTCDate(today.getUTCDate() - 1);
        s.setTime(e.getTime());
      }
      s.setUTCDate(s.getUTCDate() - 6);
      break;
    case 'last30':
      if (completedDaysOnly) {
        e.setUTCDate(today.getUTCDate() - 1);
        s.setTime(e.getTime());
      }
      s.setUTCDate(s.getUTCDate() - 29);
      break;
    case 'mtd':
      s.setUTCDate(1);
      if (completedDaysOnly) {
        e.setUTCDate(today.getUTCDate() - 1);
        disabled = e < s;
      }
      break;
    case 'lastmonth': s.setUTCMonth(today.getUTCMonth() - 1, 1); e.setUTCMonth(today.getUTCMonth(), 0); break;
    case 'qtd': {
      const q = Math.floor(today.getUTCMonth() / 3);
      s.setUTCMonth(q * 3, 1);
      if (completedDaysOnly) {
        e.setUTCDate(today.getUTCDate() - 1);
        disabled = e < s;
      }
      break;
    }
    case 'lastq': { const q = Math.floor(today.getUTCMonth() / 3); s.setUTCMonth((q - 1) * 3, 1); e.setUTCMonth(q * 3, 0); break; }
    default:
      if (completedDaysOnly) {
        e.setUTCDate(today.getUTCDate() - 1);
        s.setTime(e.getTime());
      }
      s.setUTCDate(s.getUTCDate() - 29);
  }
  const sy = ymd(s), ey = ymd(e);
  const range = {
    start: new Date(nyMidnightMs(sy.y, sy.m, sy.d)),
    end: new Date(nyMidnightMs(ey.y, ey.m, ey.d)),
  };
  return completedDaysOnly ? { ...range, disabled } : range;
}

export interface DateFilterProps {
  value: DateFilterValue;
  onChange: (value: DateFilterValue) => void;
  completedDaysOnly?: boolean;
}

export function DateFilter({ value, onChange, completedDaysOnly = false }: DateFilterProps) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const ref = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  useOutsideClick(ref, () => setOpen(false));
  const [customStart, setCustomStart] = useState(fmtInput(value.start));
  const [customEnd, setCustomEnd] = useState(fmtInput(value.end));
  useEffect(() => { setCustomStart(fmtInput(value.start)); setCustomEnd(fmtInput(value.end)); }, [value]);

  const label = value.preset === 'custom'
    ? `${fmtDate(value.start)} – ${fmtDate(value.end)}`
    : (DATE_PRESETS.find((p) => p.id === value.preset)?.label || 'Custom');

  const openIt = () => {
    if (!open && triggerRef.current) {
      const r = triggerRef.current.getBoundingClientRect();
      setPos({ top: r.bottom + 4, left: Math.max(8, r.right - 320) });
    }
    setOpen(!open);
  };
  const applyPreset = (id: string) => {
    const r = resolveRange(id, new Date(), { completedDaysOnly });
    if (r.disabled) return;
    onChange({ preset: id, start: r.start, end: r.end });
    setOpen(false);
  };
  const applyCustom = () => {
    // Interpret the picked YYYY-MM-DD as NY calendar days.
    const sMs = nyDateInputToMs(customStart), eMs = nyDateInputToMs(customEnd);
    if (!isNaN(sMs) && !isNaN(eMs)) {
      onChange({ preset: 'custom', start: new Date(sMs), end: new Date(eMs) });
      setOpen(false);
    }
  };

  return (
    <div className="dd" ref={ref}>
      <button ref={triggerRef} className={`dd-trigger ${open ? 'open' : ''}`} onClick={openIt}>
        <span className="lbl">DATE</span>
        <span style={{ display: 'inline-flex', color: 'var(--ink-2)' }}>{Icons.calendar}</span>
        <span className="val">{label}</span>
        <span className="chev">{Icons.chev_d}</span>
      </button>
      {open && (
        <ResponsivePicker label={"Date range"} onClose={() => setOpen(false)}><div className="dd-pop" style={{ top: pos.top, left: pos.left, minWidth: 320, padding: 0 }}>
          <div style={{ padding: 4 }}>
            <div className="dd-sect">Preset</div>
            {DATE_PRESETS.map((p) => {
              const range = resolveRange(p.id, new Date(), { completedDaysOnly });
              const isDisabled = range.disabled === true;
              const preview = range.start.getTime() === range.end.getTime()
                ? fmtDate(range.start).replace(/, \d{4}/, '')
                : `${fmtDate(range.start).replace(/, \d{4}/, '')} – ${fmtDate(range.end).replace(/, \d{4}/, '')}`;
              return (
                <button
                  type="button"
                  key={p.id}
                  className={`dd-item dd-date-preset ${value.preset === p.id ? 'on' : ''} ${isDisabled ? 'is-disabled' : ''}`}
                  aria-disabled={isDisabled || undefined}
                  disabled={isDisabled}
                  onClick={isDisabled ? undefined : () => applyPreset(p.id)}
                >
                  <span>{p.label}</span>
                  <span className="dd-date-preset-preview">
                    {isDisabled ? 'No completed days yet' : preview}
                  </span>
                  {value.preset === p.id && <span className="check">{Icons.check}</span>}
                </button>
              );
            })}
          </div>
          <div className="dd-sect" style={{ borderTop: '1px solid var(--line)' }}>Custom range</div>
          <div className="dd-range">
            <div><div className="field-lbl">Start</div><input type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)}/></div>
            <div><div className="field-lbl">End</div><input type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)}/></div>
          </div>
          <div className="dd-foot">
            <button className="btn btn-sm btn-ghost" onClick={() => setOpen(false)}>Cancel</button>
            <button className="btn btn-sm btn-primary" onClick={applyCustom}>Apply</button>
          </div>
        </div></ResponsivePicker>
      )}
    </div>
  );
}

export { fmtDate, fmtInput };
